import { useState, useEffect, useCallback } from 'react'
import { supabase } from './supabase'
import {
  Order,
  OrderItem,
  OrderItemOption,
  KitchenStation,
  VInventoryDashboard,
  VInventoryAlert,
  InventoryMovement,
  VMenuProfitability,
} from './types'
import {
  recalculateOrderStatus,
  addTrackingEvent,
  addOrderLog,
} from './fulfillment'

// ─── Shared column lists for performance (avoid select('*')) ───
const ORDER_COLS =
  'id, order_number, restaurant_id, status, customer_name, customer_phone, delivery_type, delivery_address, delivery_lat, delivery_lng, total, subtotal, delivery_fee, discount_amount, table_id, source, notes, tracking_token, payment_method, driver_id, picked_up_at, delivered_at, created_at, updated_at'
const ORDER_ITEM_COLS =
  'id, order_id, menu_item_id, item_name_snapshot, quantity, unit_price_snapshot, line_total, status, station_code, notes'
const ORDER_ITEM_OPTION_COLS =
  'id, order_item_id, option_name, option_price, quantity'

export interface OrderWithDetails extends Order {
  items: (OrderItem & { options: OrderItemOption[] })[]
}

export interface ActionResult {
  success: boolean
  error?: string
}

export function useAdminOrders(restaurantId: string | undefined) {
  const [orders, setOrders] = useState<OrderWithDetails[]>([])
  const [loading, setLoading] = useState(true)

  const assembleOrders = useCallback(async (ordersData: any[]) => {
    if (!ordersData || ordersData.length === 0) return []
    const orderIds = ordersData.map((o) => o.id)
    const { data: itemsData } = await supabase
      .from('order_items')
      .select('*')
      .in('order_id', orderIds)
    const itemIds = itemsData?.map((i) => i.id) || []
    let optionsData: OrderItemOption[] = []
    if (itemIds.length > 0) {
      const { data: optData } = await supabase
        .from('order_item_options')
        .select('*')
        .in('order_item_id', itemIds)
      optionsData = optData || []
    }
    return ordersData.map((order) => {
      const orderItems = (itemsData || []).filter(
        (item) => item.order_id === order.id,
      )
      return {
        ...order,
        items: orderItems.map((item) => ({
          ...item,
          options: optionsData.filter((opt) => opt.order_item_id === item.id),
        })),
      }
    })
  }, [])

  useEffect(() => {
    if (!restaurantId) return

    async function fetchOrders() {
      try {
        setLoading(true)
        console.log(
          '[NOIZA][KDS] Fetching orders for restaurant:',
          restaurantId,
        )

        // Primary: try v_orders_active view
        let { data: ordersData, error: ordersError } = await supabase
          .from('v_orders_active')
          .select('*')
          .eq('restaurant_id', restaurantId)
          .order('created_at', { ascending: false })
          .limit(50)

        // Fallback: if view fails or returns nothing, query orders table directly
        if (ordersError || !ordersData || ordersData.length === 0) {
          if (ordersError) {
            console.warn(
              '[NOIZA][KDS] v_orders_active failed:',
              ordersError.message,
              '— falling back to orders table',
            )
          } else {
            console.log(
              '[NOIZA][KDS] v_orders_active returned 0 rows — trying orders table fallback',
            )
          }

          const { data: fallbackData, error: fallbackError } = await supabase
            .from('orders')
            .select('*')
            .eq('restaurant_id', restaurantId)
            .in('status', [
              'new',
              'accepted',
              'preparing',
              'ready',
              'searching_driver',
              'out_for_delivery',
            ])
            .order('created_at', { ascending: false })
            .limit(50)

          if (fallbackError) {
            console.error(
              '[NOIZA][KDS] orders table fallback also failed:',
              fallbackError.message,
            )
          } else {
            ordersData = fallbackData
            ordersError = null
            console.log(
              '[NOIZA][KDS] Fallback returned',
              fallbackData?.length || 0,
              'orders',
            )
          }
        }

        if (ordersError) throw ordersError

        if (!ordersData || ordersData.length === 0) {
          console.log('[NOIZA][KDS] No active orders found')
          setOrders([])
          return
        }

        console.log(
          '[NOIZA][KDS] Fetched',
          ordersData.length,
          'orders:',
          ordersData.map((o: any) => `${o.order_number}:${o.status}`),
        )
        const assembled = await assembleOrders(ordersData)
        setOrders(assembled)
      } catch (error) {
        console.error('[NOIZA][KDS] Error fetching admin orders:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchOrders()

    // ─── Realtime subscription for instant order updates ───
    const channelName = `kds_orders_${restaurantId}_${Date.now()}`
    const channel = supabase
      .channel(channelName)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'orders',
          filter: `restaurant_id=eq.${restaurantId}`,
        },
        (payload) => {
          console.log(
            '[NOIZA][KDS] 🔔 Realtime: NEW ORDER received:',
            payload.new?.order_number || payload.new?.id,
          )
          fetchOrders()
        },
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'orders',
          filter: `restaurant_id=eq.${restaurantId}`,
        },
        (payload) => {
          console.log(
            '[NOIZA][KDS] 🔔 Realtime: ORDER UPDATED:',
            payload.new?.order_number || payload.new?.id,
            '→',
            payload.new?.status,
          )
          fetchOrders()
        },
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'order_items',
          // NOTE: order_items does not have restaurant_id column, so we cannot filter here.
          // This listener receives updates from ALL restaurants. The fetchOrders() call
          // re-queries with restaurant_id filter, so data integrity is maintained.
          // Future optimization: add restaurant_id to order_items or use a view.
        },
        (payload) => {
          console.log(
            '[NOIZA][KDS] 🔔 Realtime: ORDER ITEM UPDATED:',
            payload.new?.id,
            '→',
            payload.new?.status,
          )
          fetchOrders()
        },
      )
      .subscribe((status) => {
        console.log('[NOIZA][KDS] Realtime subscription status:', status)
      })

    // Keep polling as backup (30s instead of 15s since realtime handles most updates)
    const interval = setInterval(fetchOrders, 30000)

    return () => {
      clearInterval(interval)
      supabase.removeChannel(channel)
    }
  }, [restaurantId, assembleOrders])

  // ─── Refetch helper ────────────────────────────────────────
  const refetchOrders = useCallback(async () => {
    try {
      // Primary: try v_orders_active view
      let { data: ordersData, error: ordersError } = await supabase
        .from('v_orders_active')
        .select('*')
        .eq('restaurant_id', restaurantId)
        .order('created_at', { ascending: false })
        .limit(50)

      // Fallback: if view fails, query orders table directly
      if (ordersError) {
        console.warn(
          '[NOIZA][KDS] refetch v_orders_active failed:',
          ordersError.message,
          '— using fallback',
        )
        const { data: fallbackData, error: fallbackError } = await supabase
          .from('orders')
          .select('*')
          .eq('restaurant_id', restaurantId)
          .in('status', [
            'new',
            'accepted',
            'preparing',
            'ready',
            'searching_driver',
            'out_for_delivery',
          ])
          .order('created_at', { ascending: false })
          .limit(50)

        if (fallbackError) throw fallbackError
        ordersData = fallbackData
        ordersError = null
      }

      if (ordersError) throw ordersError

      if (!ordersData || ordersData.length === 0) {
        setOrders([])
        return
      }

      const assembled = await assembleOrders(ordersData)
      setOrders(assembled)
    } catch (error) {
      console.error('[NOIZA][KDS] Error refetching orders:', error)
    }
  }, [restaurantId, assembleOrders])

  // ─── Optimistic update helpers ─────────────────────────────
  const optimisticUpdateOrder = useCallback(
    (orderId: string, status: string) => {
      setOrders((prev) =>
        prev.map((o) => (o.id === orderId ? { ...o, status } : o)),
      )
    },
    [],
  )

  const optimisticUpdateItem = useCallback((itemId: string, status: string) => {
    setOrders((prev) =>
      prev.map((order) => ({
        ...order,
        items: order.items.map((item) =>
          item.id === itemId ? { ...item, status } : item,
        ),
      })),
    )
  }, [])

  // ─── RPC with fallback wrapper ─────────────────────────────
  const tryRpcWithFallback = useCallback(
    async (
      rpcName: string,
      params: Record<string, any>,
      fallback: () => Promise<void>,
    ): Promise<ActionResult> => {
      try {
        const { error: rpcError } = await supabase.rpc(rpcName, params)
        if (rpcError) {
          console.warn(
            `[NOIZA] RPC ${rpcName} failed, using fallback:`,
            rpcError,
          )
          await fallback()
        }
        await refetchOrders()
        return { success: true }
      } catch (err: any) {
        console.error(`[NOIZA] ${rpcName} error:`, err)
        return { success: false, error: err.message }
      }
    },
    [refetchOrders],
  )

  // ─── Helper to get restaurant_id from order ────────────────
  const getOrderRestaurantId = useCallback(
    async (orderId: string): Promise<string | null> => {
      // Try from local state first
      const localOrder = orders.find((o) => o.id === orderId)
      if (localOrder) return localOrder.restaurant_id
      // Fallback to DB
      const { data } = await supabase
        .from('orders')
        .select('restaurant_id')
        .eq('id', orderId)
        .single()
      return data?.restaurant_id || null
    },
    [orders],
  )

  // ─── Order-level actions with fallback ─────────────────────
  const acceptOrder = useCallback(
    async (orderId: string): Promise<ActionResult> => {
      console.log('[NOIZA] acceptOrder:', orderId)
      optimisticUpdateOrder(orderId, 'accepted')
      const result = await tryRpcWithFallback(
        'accept_order',
        { p_order_id: orderId },
        async () => {
          const { error } = await supabase
            .from('orders')
            .update({ status: 'accepted' })
            .eq('id', orderId)
          if (error) throw error
        },
      )
      // Sync tracking & logs
      if (result.success) {
        const rid = await getOrderRestaurantId(orderId)
        if (rid) {
          await addTrackingEvent(orderId, rid, 'accepted')
          await addOrderLog(orderId, rid, 'order_accepted', { trigger: 'kds' })
        }
      }
      return result
    },
    [tryRpcWithFallback, optimisticUpdateOrder, getOrderRestaurantId],
  )

  const startOrderPreparation = useCallback(
    async (orderId: string): Promise<ActionResult> => {
      console.log('[NOIZA] startOrderPreparation:', orderId)
      optimisticUpdateOrder(orderId, 'preparing')
      const result = await tryRpcWithFallback(
        'start_order_preparation',
        { p_order_id: orderId },
        async () => {
          const { error: orderError } = await supabase
            .from('orders')
            .update({ status: 'preparing' })
            .eq('id', orderId)
          if (orderError) throw orderError

          const { error: itemsError } = await supabase
            .from('order_items')
            .update({ status: 'preparing' })
            .eq('order_id', orderId)
          if (itemsError) throw itemsError
        },
      )
      // Sync tracking & logs
      if (result.success) {
        const rid = await getOrderRestaurantId(orderId)
        if (rid) {
          await addTrackingEvent(orderId, rid, 'preparing')
          await addOrderLog(orderId, rid, 'order_preparing', { trigger: 'kds' })
        }
      }
      return result
    },
    [tryRpcWithFallback, optimisticUpdateOrder, getOrderRestaurantId],
  )

  const markOrderReady = useCallback(
    async (orderId: string): Promise<ActionResult> => {
      console.log('[NOIZA] markOrderReady:', orderId)
      optimisticUpdateOrder(orderId, 'ready')
      const result = await tryRpcWithFallback(
        'mark_order_ready',
        { p_order_id: orderId },
        async () => {
          const { error: orderError } = await supabase
            .from('orders')
            .update({ status: 'ready' })
            .eq('id', orderId)
          if (orderError) throw orderError

          const { error: itemsError } = await supabase
            .from('order_items')
            .update({ status: 'ready' })
            .eq('order_id', orderId)
          if (itemsError) throw itemsError
        },
      )
      // Sync tracking & logs
      if (result.success) {
        const rid = await getOrderRestaurantId(orderId)
        if (rid) {
          await addTrackingEvent(orderId, rid, 'ready')
          await addOrderLog(orderId, rid, 'order_ready', { trigger: 'kds' })
        }
        // Also trigger fulfillment recalculation (handles delivery job creation)
        await recalculateOrderStatus(orderId)
        await refetchOrders()
      }
      return result
    },
    [
      tryRpcWithFallback,
      optimisticUpdateOrder,
      getOrderRestaurantId,
      refetchOrders,
    ],
  )

  const deliverOrder = useCallback(
    async (orderId: string): Promise<ActionResult> => {
      console.log('[NOIZA] deliverOrder:', orderId)
      // Delivery orders are handled manually by the restaurant — no driver flow.
      optimisticUpdateOrder(orderId, 'delivered')
      const result = await tryRpcWithFallback(
        'deliver_order',
        { p_order_id: orderId },
        async () => {
          const { error } = await supabase
            .from('orders')
            .update({ status: 'delivered' })
            .eq('id', orderId)
          if (error) throw error
        },
      )
      // Sync tracking & logs
      if (result.success) {
        const rid = await getOrderRestaurantId(orderId)
        if (rid) {
          await addTrackingEvent(orderId, rid, 'delivered')
          await addOrderLog(orderId, rid, 'order_delivered', { trigger: 'kds' })
        }
      }
      return result
    },
    [optimisticUpdateOrder, getOrderRestaurantId],
  )

  const cancelOrder = useCallback(
    async (orderId: string): Promise<ActionResult> => {
      console.log('[NOIZA] cancelOrder:', orderId)
      optimisticUpdateOrder(orderId, 'cancelled')
      const result = await tryRpcWithFallback(
        'cancel_order',
        { p_order_id: orderId },
        async () => {
          const { error: orderError } = await supabase
            .from('orders')
            .update({ status: 'cancelled' })
            .eq('id', orderId)
          if (orderError) throw orderError

          const { error: itemsError } = await supabase
            .from('order_items')
            .update({ status: 'cancelled' })
            .eq('order_id', orderId)
          if (itemsError) throw itemsError
        },
      )
      // Sync tracking & logs
      if (result.success) {
        const rid = await getOrderRestaurantId(orderId)
        if (rid) {
          await addTrackingEvent(orderId, rid, 'cancelled')
          await addOrderLog(orderId, rid, 'order_cancelled', { trigger: 'kds' })

          // Register cancellation record (idempotent — skips if already exists)
          const { error: cancelRecordError } = await supabase
            .from('order_cancellations')
            .upsert(
              {
                order_id: orderId,
                restaurant_id: rid,
                cancelled_by: 'system',
                reason: null,
                notes: null,
              },
              {
                onConflict: 'order_id',
                ignoreDuplicates: true,
              },
            )

          if (cancelRecordError) {
            console.warn(
              '[NOIZA] Could not insert order_cancellations record:',
              cancelRecordError,
            )
          }
        }
      }
      return result
    },
    [tryRpcWithFallback, optimisticUpdateOrder, getOrderRestaurantId],
  )

  // ─── Item-level actions using KDS RPCs ─────────────────────
  const markItemPreparing = useCallback(
    async (itemId: string): Promise<ActionResult> => {
      console.log('[NOIZA] ═══════════════════════════════════')
      console.log('[NOIZA] 🔥 markItemPreparing CALLED')
      console.log('[NOIZA] Raw itemId:', itemId)
      console.log('[NOIZA] itemId type:', typeof itemId)

      const numericId = parseInt(itemId, 10)
      console.log('[NOIZA] Parsed numeric ID:', numericId)
      console.log('[NOIZA] Is valid number:', !isNaN(numericId))

      if (isNaN(numericId)) {
        console.error('[NOIZA] ❌ INVALID ID - cannot parse to number')
        return { success: false, error: `Invalid item ID: ${itemId}` }
      }

      try {
        console.log('[NOIZA] 📡 Calling Supabase RPC: kds_start_item')
        console.log('[NOIZA] RPC params:', { p_order_item_id: numericId })

        const { data, error } = await supabase.rpc('kds_start_item', {
          p_order_item_id: numericId,
        })

        console.log('[NOIZA] RPC response data:', data)
        console.log('[NOIZA] RPC response error:', error)

        if (error) {
          console.error('[NOIZA] ❌ kds_start_item RPC error:', error)
          console.error(
            '[NOIZA] Error details:',
            JSON.stringify(error, null, 2),
          )
          return { success: false, error: error.message }
        }

        console.log('[NOIZA] ✅ RPC success, refetching orders...')
        await refetchOrders()
        console.log('[NOIZA] ✅ Refetch complete')
        console.log('[NOIZA] ═══════════════════════════════════')
        return { success: true }
      } catch (err: any) {
        console.error('[NOIZA] ❌ EXCEPTION in markItemPreparing:', err)
        console.error('[NOIZA] Exception stack:', err.stack)
        console.error('[NOIZA] ═══════════════════════════════════')
        return { success: false, error: err.message }
      }
    },
    [refetchOrders],
  )

  const markItemReady = useCallback(
    async (itemId: string): Promise<ActionResult> => {
      console.log('[NOIZA] ═══════════════════════════════════')
      console.log('[NOIZA] ✅ markItemReady CALLED')
      console.log('[NOIZA] Raw itemId:', itemId)
      console.log('[NOIZA] itemId type:', typeof itemId)

      const numericId = parseInt(itemId, 10)
      console.log('[NOIZA] Parsed numeric ID:', numericId)
      console.log('[NOIZA] Is valid number:', !isNaN(numericId))

      if (isNaN(numericId)) {
        console.error('[NOIZA] ❌ INVALID ID - cannot parse to number')
        return { success: false, error: `Invalid item ID: ${itemId}` }
      }

      try {
        console.log('[NOIZA] 📡 Calling Supabase RPC: kds_mark_item_ready')
        console.log('[NOIZA] RPC params:', { p_order_item_id: numericId })

        const { data, error } = await supabase.rpc('kds_mark_item_ready', {
          p_order_item_id: numericId,
        })

        console.log('[NOIZA] RPC response data:', data)
        console.log('[NOIZA] RPC response error:', error)

        if (error) {
          console.error('[NOIZA] ❌ kds_mark_item_ready RPC error:', error)
          console.error(
            '[NOIZA] Error details:',
            JSON.stringify(error, null, 2),
          )
          return { success: false, error: error.message }
        }

        console.log('[NOIZA] ✅ RPC success, refetching orders...')
        await refetchOrders()

        // FULFILLMENT: Recalculate order status after item marked ready
        if (data && data.order_id) {
          console.log(
            '[NOIZA] 🔄 Triggering fulfillment recalculation for order:',
            data.order_id,
          )
          await recalculateOrderStatus(data.order_id)
          await refetchOrders() // Refetch again to show new order status
        }

        console.log('[NOIZA] ✅ Refetch complete')
        console.log('[NOIZA] ═══════════════════════════════════')
        return { success: true }
      } catch (err: any) {
        console.error('[NOIZA] ❌ EXCEPTION in markItemReady:', err)
        console.error('[NOIZA] Exception stack:', err.stack)
        console.error('[NOIZA] ═══════════════════════════════════')
        return { success: false, error: err.message }
      }
    },
    [refetchOrders],
  )

  // ─── Mark all station items ready ───────────────────────────
  const markStationItemsReady = useCallback(
    async (orderId: string, stationCode: string): Promise<ActionResult> => {
      console.log('[NOIZA] markStationItemsReady:', { orderId, stationCode })
      const isAllStations = stationCode === 'all' || stationCode === 'caja'

      try {
        // 1. Get all items for this station (or all stations) in this order
        let query = supabase
          .from('order_items')
          .select('id, status')
          .eq('order_id', orderId)
          .neq('status', 'ready')
          .neq('status', 'served')

        if (!isAllStations) {
          query = query.eq('station_code', stationCode)
        }

        const { data: stationItems, error: fetchError } = await query

        if (fetchError) {
          console.error('[NOIZA] Error fetching station items:', fetchError)
          return { success: false, error: fetchError.message }
        }

        if (!stationItems || stationItems.length === 0) {
          console.log('[NOIZA] No items to mark ready')
          await refetchOrders()
          return { success: true }
        }

        // 2. Mark each item ready using the RPC (with direct update fallback)
        console.log(`[NOIZA] Marking ${stationItems.length} items ready`)
        for (const item of stationItems) {
          const { error: rpcError } = await supabase.rpc(
            'kds_mark_item_ready',
            {
              p_order_item_id: item.id,
            },
          )

          if (rpcError) {
            console.warn(
              `[NOIZA] RPC failed for item ${item.id}:`,
              rpcError.message,
              '— using direct update fallback',
            )
            // Fallback: direct update
            const { error: updateError } = await supabase
              .from('order_items')
              .update({ status: 'ready' })
              .eq('id', item.id)

            if (updateError) {
              console.error(
                `[NOIZA] Fallback update also failed for item ${item.id}:`,
                updateError,
              )
            } else {
              console.log(
                `[NOIZA] Fallback update succeeded for item ${item.id}`,
              )
            }
          }
        }

        // 3. Refetch to update UI
        await refetchOrders()

        // FULFILLMENT: Recalculate order status after station items marked ready
        console.log(
          '[NOIZA] 🔄 Triggering fulfillment recalculation for order:',
          orderId,
        )
        await recalculateOrderStatus(orderId)
        await refetchOrders() // Refetch again to show new order status

        return { success: true }
      } catch (err: any) {
        console.error('[NOIZA] markStationItemsReady error:', err)
        return { success: false, error: err.message }
      }
    },
    [refetchOrders],
  )

  // Legacy — kept for backward compatibility
  const updateOrderStatus = useCallback(
    async (orderId: string, status: string) => {
      try {
        const { error } = await supabase
          .from('orders')
          .update({ status })
          .eq('id', orderId)
        if (error) throw error
        await refetchOrders()
      } catch (error) {
        console.error('Error updating order status:', error)
      }
    },
    [refetchOrders],
  )

  const updateItemStatus = useCallback(
    async (itemId: string, status: string, orderId: string) => {
      try {
        const { error } = await supabase
          .from('order_items')
          .update({ status })
          .eq('id', itemId)
        if (error) throw error
        await refetchOrders()
      } catch (error) {
        console.error('[NOIZA] Error updating item status:', error)
      }
    },
    [refetchOrders],
  )

  return {
    orders,
    loading,
    updateOrderStatus,
    updateItemStatus,
    acceptOrder,
    startOrderPreparation,
    markOrderReady,
    deliverOrder,
    cancelOrder,
    markItemReady,
    markStationItemsReady,
    refetchOrders,
    setOrders,
  }
}

// ─── Kitchen Stations Hook ──────────────────────────────────

export function useKitchenStations(restaurantId: string | undefined) {
  const [stations, setStations] = useState<KitchenStation[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!restaurantId) return

    async function fetchStations() {
      try {
        const { data, error } = await supabase
          .from('kitchen_stations')
          .select(
            'id, restaurant_id, name, code, sort_order, is_active, created_at',
          )
          .eq('restaurant_id', restaurantId)
          .eq('is_active', true)
          .order('sort_order', { ascending: true })

        if (error) throw error
        setStations(data || [])
      } catch (error) {
        console.error('Error fetching kitchen stations:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchStations()
  }, [restaurantId])

  return { stations, loading }
}

// ─── Inventory Dashboard Hook ───────────────────────────────

export function useInventoryDashboard(restaurantId: string | undefined) {
  const [items, setItems] = useState<VInventoryDashboard[]>([])
  const [alerts, setAlerts] = useState<VInventoryAlert[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!restaurantId) return

    async function fetchInventory() {
      try {
        const [dashboardRes, alertsRes] = await Promise.all([
          supabase
            .from('v_inventory_dashboard')
            .select('*')
            .eq('restaurant_id', restaurantId)
            .order('ingredient_name', { ascending: true })
            .limit(500),
          supabase
            .from('v_inventory_alerts')
            .select('*')
            .eq('restaurant_id', restaurantId)
            .order('alert_level', { ascending: false })
            .limit(100),
        ])

        if (dashboardRes.error) throw dashboardRes.error
        if (alertsRes.error) throw alertsRes.error

        setItems(dashboardRes.data || [])
        setAlerts(alertsRes.data || [])
      } catch (error) {
        console.error('Error fetching inventory:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchInventory()

    const interval = setInterval(fetchInventory, 30000)
    return () => clearInterval(interval)
  }, [restaurantId])

  return { items, alerts, loading }
}

// ─── Inventory Movements Hook ───────────────────────────────

export function useInventoryMovements(
  restaurantId: string | undefined,
  itemId?: string,
) {
  const [movements, setMovements] = useState<InventoryMovement[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!restaurantId) return

    async function fetchMovements() {
      try {
        let query = supabase
          .from('inventory_movements')
          .select('*')
          .eq('restaurant_id', restaurantId)
          .order('created_at', { ascending: false })
          .limit(50)

        if (itemId) {
          query = query.eq('inventory_item_id', itemId)
        }

        const { data, error } = await query

        if (error) throw error
        setMovements(data || [])
      } catch (error) {
        console.error('Error fetching inventory movements:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchMovements()
  }, [restaurantId, itemId])

  return { movements, loading }
}

// ─── Menu Profitability Hook ────────────────────────────────

export function useMenuProfitability(restaurantId: string | undefined) {
  const [items, setItems] = useState<VMenuProfitability[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!restaurantId) return

    async function fetchProfitability() {
      try {
        const { data, error } = await supabase
          .from('v_menu_profitability')
          .select('*')
          .eq('restaurant_id', restaurantId)
          .order('margin_percent', { ascending: false })

        if (error) throw error
        setItems(data || [])
      } catch (error) {
        console.error('Error fetching menu profitability:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchProfitability()
  }, [restaurantId])

  return { items, loading }
}

// ─── Inventory Data Hook (for DashboardPage) ────────────────

export function useInventoryData(restaurantId: string | undefined) {
  const [alerts, setAlerts] = useState<VInventoryAlert[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!restaurantId) return

    async function fetchAlerts() {
      try {
        const { data, error } = await supabase
          .from('v_inventory_alerts')
          .select('*')
          .eq('restaurant_id', restaurantId)
          .order('alert_level', { ascending: false })

        if (error) throw error
        setAlerts(data || [])
      } catch (error) {
        console.error('Error fetching inventory alerts:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchAlerts()
  }, [restaurantId])

  return { alerts, loading }
}

// ─── Metrics Data Hook (for MetricsPage) ────────────────────

export function useMetricsData(restaurantId: string | undefined) {
  const [profitability, setProfitability] = useState<VMenuProfitability[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!restaurantId) return

    async function fetchMetrics() {
      try {
        const { data, error } = await supabase
          .from('v_menu_profitability')
          .select('*')
          .eq('restaurant_id', restaurantId)
          .order('margin_percent', { ascending: false })

        if (error) throw error
        setProfitability(data || [])
      } catch (error) {
        console.error('Error fetching metrics:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchMetrics()
  }, [restaurantId])

  return { profitability, loading }
}

// ─── Daily Sales Summary Hook ───────────────────────────────

export function useDailySalesSummary(restaurantId: string | undefined) {
  const [summary, setSummary] = useState<{
    orders_today: number
    subtotal_today: number
    delivery_today: number
    total_today: number
  } | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!restaurantId) return

    async function fetchSummary() {
      try {
        setLoading(true)
        const { data, error } = await supabase
          .from('v_sales_summary_today')
          .select('*')
          .eq('restaurant_id', restaurantId)
          .maybeSingle()

        if (error) throw error

        setSummary(
          data || {
            orders_today: 0,
            subtotal_today: 0,
            delivery_today: 0,
            total_today: 0,
          },
        )
      } catch (error) {
        console.error('Error fetching daily sales summary:', error)
        setSummary({
          orders_today: 0,
          subtotal_today: 0,
          delivery_today: 0,
          total_today: 0,
        })
      } finally {
        setLoading(false)
      }
    }

    fetchSummary()
  }, [restaurantId])

  return { summary, loading }
}

// ─── Cancelled Orders Hook ──────────────────────────────────

export function useCancelledOrders(restaurantId: string | undefined) {
  const [orders, setOrders] = useState<OrderWithDetails[]>([])
  const [loading, setLoading] = useState(true)

  const assembleOrders = useCallback(async (ordersData: any[]) => {
    if (!ordersData || ordersData.length === 0) return []

    const orderIds = ordersData.map((o) => o.id)

    const { data: itemsData } = await supabase
      .from('order_items')
      .select('*')
      .in('order_id', orderIds)

    const itemIds = itemsData?.map((i) => i.id) || []
    let optionsData: OrderItemOption[] = []
    if (itemIds.length > 0) {
      const { data: optData } = await supabase
        .from('order_item_options')
        .select('*')
        .in('order_item_id', itemIds)
      optionsData = optData || []
    }

    return ordersData.map((order) => {
      const orderItems = (itemsData || []).filter(
        (item) => item.order_id === order.id,
      )
      return {
        ...order,
        items: orderItems.map((item) => ({
          ...item,
          options: optionsData.filter((opt) => opt.order_item_id === item.id),
        })),
      }
    })
  }, [])

  useEffect(() => {
    if (!restaurantId) return

    async function fetchCancelled() {
      try {
        setLoading(true)
        const { data, error } = await supabase
          .from('v_orders_cancelled')
          .select('*')
          .eq('restaurant_id', restaurantId)
          .order('created_at', { ascending: false })
          .limit(50)

        if (error) throw error

        const assembled = await assembleOrders(data || [])
        setOrders(assembled)
      } catch (error) {
        console.error('Error fetching cancelled orders:', error)
        setOrders([])
      } finally {
        setLoading(false)
      }
    }

    fetchCancelled()
  }, [restaurantId, assembleOrders])

  return { orders, loading }
}
